/* eslint no-underscore-dangle: ["error", { "allow": ["_id"] }] */
import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { STORAGE_KEYS } from '../../constants/defaultValues';
import { managementRoles } from '../../constants/roles';
import usePermissions from '../../hooks/usePermissions';
import { tagsColor } from '../../helpers/tagsColors';
import { getAgentsByDepartments } from '../../services/getAgentsByDepartments';
import { getDepartments } from '../../services/getDepartments';
import { getListTags } from '../../services/getTags';
import { TagsType } from '../../types/tags';
import AuthContext from '../auth/context';
import TransferContext from './context';
import { Department, User } from '../../services/types';
import { AvailableUser, getAvailableUsers } from '../../services/getAvailableUsers';
import { getLiveChatUsersAgent } from '../../services/getLivechatUsersAgt';
import { UserStatus, UserStatusLivechat } from '../../types/user';

export type IAgentOfDepartment = {
  name: string;
  _id: string;
  status: UserStatus;
  username: string;
  departmentId?: string;
};

const TransferProvider: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;

  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  const [tags, setTags] = useState<Array<TagsType>>([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [isReturnToQueue, setIsReturnToQueue] = useState(false);
  const [loading, setLoading] = useState(true);
  const { getCurrentUserId, currentUserInfo } = useContext(AuthContext);
  const [AllAgentsOfDepartment, setAllAgentsOfDepartment] = useState<IAgentOfDepartment[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [pagination, setPagination] = useState({ count: 100, offset: 0 });
  const [userId, setUserId] = useState('');
  const { checkPermission } = usePermissions();
  const canSelfTransfer = checkPermission(managementRoles);
  const botDptName = STORAGE_KEYS.BOT_DEPARTMENT_NAME;

  const loadTags = useCallback(async () => {
    const { data } = await getListTags();
    const { tags, total } = data;
    if (!tags) {
      return;
    }
    const mappedTags = tags
      .filter((item: TagsType) => {
        if (item.description === 'room') {
          return item;
        }
      })
      .map((item: TagsType) => {
        item.customColor = tagsColor(item.name);
        return item;
      });

    const newArray = [...tags, ...mappedTags];
    if (pagination.count < total) {
      setPagination((old) => ({
        ...old,
        offset: tags.length,
        count: pagination.count + tags.length,
      }));

      setTags(newArray);
    }
  }, [pagination.count]);

  const loadDepartments = useCallback(
    async (departmentId: string) => {
      const {
        data: { departments },
      } = await getDepartments();

      const finalDepartments = departments.filter((el) => el._id !== departmentId && el.name !== botDptName);

      setAllDepartments(finalDepartments);

      setLoading(false);
    },
    [currentUserInfo.roles]
  );

  const loadAgents = useCallback(
    async (departmentId: string, term?: string) => {
      setIsLoadingAgents(true);
      try {
        const {
          data: { agents },
        } = await getAgentsByDepartments(departmentId);

        let availableUsers: Array<User | AvailableUser> | undefined;
        if (userId && agents.length > 0 && term) {
          // Use the auto-complete endpoint, but only get first 10
          const options = {
            filter: term,
            loggedUser: userId,
            statusLivechat: UserStatusLivechat.Available,
          };

          const { data } = await getAvailableUsers(options);
          availableUsers = data.items;
        }

        if (userId && agents.length > 0 && !term) {
          const { data } = await getLiveChatUsersAgent({
            excludeId: canSelfTransfer ? undefined : userId,
            onlyAvailable: true,
            showIdleAgents: false,
          });

          availableUsers = data.users;
        }

        if (availableUsers) {
          const list = availableUsers
            .filter((availableUser) => agents.some((agent) => agent.username === availableUser.username))
            .map((availableUser) => {
              const userInfo = agents.find((user) => user?._id !== userId);
              const agentUser = {
                name: availableUser.name,
                _id: availableUser._id,
                // We're requesting online and livechat-available users, reflect them as online regardless of their user status
                status: UserStatus.Online,
                username: availableUser.username,
                departmentId: userInfo?.departmentId,
              };

              return agentUser;
            });

          setAllAgentsOfDepartment(list);
        } else {
          setAllAgentsOfDepartment([]);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoadingAgents(false);
      }
    },
    [canSelfTransfer, userId]
  );

  const checkIfHasAgentsAvailable = useCallback(
    async (departmentId: string) => {
      if (userId) {
        const {
          data: { users },
        } = await getLiveChatUsersAgent({
          excludeId: canSelfTransfer ? undefined : userId,
          onlyAvailable: true,
          showIdleAgents: false,
        });

        return users.some((user) => user.departments?.includes(departmentId));
      }

      return false;
    },
    [canSelfTransfer, userId]
  );

  const handleSetSelectedDepartment = useCallback((departmentId: string) => {
    setSelectedDepartment(departmentId);
  }, []);

  const handleSetSelectedAgent = useCallback((agentId: string) => {
    setSelectedAgent(agentId);
  }, []);

  const clearSelectedsTransfer = useCallback(() => {
    setSelectedDepartment('');
    setSelectedAgent('');
  }, []);

  const handleReturnToQueue = useCallback(() => {
    setIsReturnToQueue((oldState) => !oldState);
  }, []);

  useEffect(() => {
    loadTags();
  }, [pagination]);

  useEffect(() => {
    const id = getCurrentUserId();
    if (id) {
      setUserId(id);
    }
  }, []);

  const memoizedValuesTransferProvider = useMemo(
    () => ({
      tags,
      loadTags,
      loadAgents,
      loadDepartments,
      allDepartments,
      AllAgentsOfDepartment,
      checkIfHasAgentsAvailable,
      handleSetSelectedDepartment,
      selectedDepartment,
      selectedAgent,
      handleSetSelectedAgent,
      clearSelectedsTransfer,
      handleReturnToQueue,
      isReturnToQueue,
      isLoadingAgents,
      loading,
    }),
    [
      tags,
      loadTags,
      loadAgents,
      loadDepartments,
      allDepartments,
      AllAgentsOfDepartment,
      checkIfHasAgentsAvailable,
      handleSetSelectedDepartment,
      selectedDepartment,
      selectedAgent,
      handleSetSelectedAgent,
      clearSelectedsTransfer,
      handleReturnToQueue,
      isReturnToQueue,
      isLoadingAgents,
      loading,
    ]
  );
  return <TransferContext.Provider value={memoizedValuesTransferProvider}>{children}</TransferContext.Provider>;
};

export default TransferProvider;
